import { Image, Film, Play, Music, FileText } from 'lucide-react';

// YouTube URL patterns
const YOUTUBE_REGEX = /(?:youtube\.com\/(?:watch\?v=|embed\/|v\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
// Vimeo URL patterns
const VIMEO_REGEX = /vimeo\.com\/(?:video\/)?(\d+)/;

const EXT_MAP = {
  image: ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'],
  video: ['mp4', 'webm'],
  audio: ['mp3', 'wav', 'ogg', 'webm'],
  pdf: ['pdf'],
};

/**
 * Detect media type from a URL string.
 * Returns 'image' | 'video' | 'youtube' | 'vimeo' | 'audio' | 'pdf' | 'unknown'
 * Fallback: 'image' for backward compatibility (existing URLs are all images).
 */
export function detectMediaType(url) {
  if (!url) return 'unknown';

  // Check YouTube/Vimeo first (before extension check)
  if (YOUTUBE_REGEX.test(url)) return 'youtube';
  if (VIMEO_REGEX.test(url)) return 'vimeo';

  // Extract extension — check ?f= query param first (proxy URLs like media.php?f=file.pdf)
  const fParam = url.match(/[?&]f=([^&#]+)/);
  const target = fParam ? decodeURIComponent(fParam[1]) : url.split('?')[0].split('#')[0];
  const ext = target.split('.').pop()?.toLowerCase();
  if (!ext) return 'image'; // fallback

  for (const [type, extensions] of Object.entries(EXT_MAP)) {
    if (extensions.includes(ext)) {
      // webm can be audio or video — treat as video for uploaded files
      if (ext === 'webm' && type === 'audio') continue;
      return type;
    }
  }

  return 'image'; // fallback for backward compat
}

/**
 * Extract YouTube video ID and return embed URL.
 */
export function getYouTubeEmbedUrl(url) {
  const match = url?.match(YOUTUBE_REGEX);
  return match ? `https://www.youtube.com/embed/${match[1]}` : null;
}

/**
 * Extract Vimeo video ID and return embed URL.
 */
export function getVimeoEmbedUrl(url) {
  const match = url?.match(VIMEO_REGEX);
  return match ? `https://player.vimeo.com/video/${match[1]}` : null;
}

/**
 * Check if a URL is a valid YouTube or Vimeo embed link.
 */
export function isEmbedUrl(url) {
  return YOUTUBE_REGEX.test(url) || VIMEO_REGEX.test(url);
}

/**
 * Normalize media URL: rewrite admin upload paths to PHP proxy URL.
 * Handles legacy (/backend/uploads/admin/), intermediate (/backend/media/), and /media/ formats.
 */
export function normalizeMediaUrl(url) {
  if (!url) return url;
  // Match /backend/uploads/admin/file, /backend/media/file, or /media/file
  const match = url.match(/(?:\/backend\/(?:uploads\/admin|media)|\/media)\/(.+)$/);
  if (match) return '/backend/media.php?f=' + encodeURIComponent(match[1]);
  return url;
}

/**
 * Download a file via fetch+blob so the URL is never exposed in the browser status bar.
 */
export async function downloadMediaBlob(url, filename) {
  try {
    const resp = await fetch(normalizeMediaUrl(url));
    const blob = await resp.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = filename || url.split('/').pop() || 'download';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(blobUrl);
  } catch {
    // Fallback: open in new tab
    window.open(normalizeMediaUrl(url), '_blank');
  }
}

// File input accept string for all supported media types
export const MEDIA_ACCEPT_STRING = 'image/*,video/mp4,video/webm,audio/mpeg,audio/wav,audio/ogg,application/pdf';

// Human-readable labels
export const MEDIA_TYPE_LABELS = {
  image: 'Image',
  video: 'Vidéo',
  youtube: 'YouTube',
  vimeo: 'Vimeo',
  audio: 'Audio',
  pdf: 'PDF',
  unknown: 'Fichier',
};

// Lucide icons per media type
export const MEDIA_TYPE_ICONS = {
  image: Image,
  video: Film,
  youtube: Play,
  vimeo: Play,
  audio: Music,
  pdf: FileText,
  unknown: FileText,
};
